import { useCallback, useRef, useState } from 'preact/hooks';
import { detectTruncation } from '#utils/PRIVATE_truncationUtils';
import { Label } from '#UNSAFE_Label/Label';
import { ReadonlyTextField } from '#UNSAFE_TextField';
import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { View } from '#UNSAFE_View';
import { useFocusWithin } from '#hooks/UNSAFE_useFocusWithin';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useFormFieldContextProps } from '#hooks/UNSAFE_useFormFieldContextProps';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { Size } from '#utils/UNSAFE_size';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { HiddenLabel } from './HiddenLabel';
import { useLabelledLinkAria } from './useLabelledLinkAria';
import { useLabelledLinkTheme } from './useLabelledLinkTheme';
import { TruncatingLink } from '#PRIVATE_TruncatingLink';
import { Link } from '#UNSAFE_Link';

export type LabelledLinkProps = {
  /**
   * Specifies the text that should appear in the field. If this property
   * is not provided, then the URL provided using the `href` property
   * will be used as the text in the field.
   */
  children?: string;

  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;

  /**
   * Specifies the label associated with the field.
   */
  label: string;

  /**
   * Specifies where the label is positioned relative to the field.
   * @default 'inside'
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';

  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;

  /**
   * Specifies how to align text within the field.
   */
  textAlign?: 'start' | 'end' | 'right';

  /**
   * The component does not support any assistive text. But, this property can be used to specify
   * whether or not the component should reserve space to be consistent with the other form components
   * in the Form Layout. It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Reserve space to prevent layout reflow.</li>
   * <li><code>'reflow'</code>: Do not reserve space.</li>
   * <li><code>'compact'</code>: Do not reserve space. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;

  /**
   * TODO: Use picked props from TruncatingLink once it's public to replace the following props. JET-77369
   */

  /**
   * Sets the URL that the hyperlink points to.
   */
  href: string;

  /**
   * Sets the target.
   */
  target?: string;

  /**
   * Triggered when a link is clicked, whether by keyboard, mouse, or touch events.
   */
  onClick?: (event: Event) => void;

  /**
   * Truncates the link at a specific number of lines and then displays an ellipsis (…) at the end of the last line. Parent of the element needs to have a specific width so the link starts overflowing and produces a truncation.
   */
  lineClamp?: number;

  /**
   * Specifies id for element (or elements) that describes the object.
   */
  'aria-describedby'?: string;
};

/**
 * LabelledLink component can be used to render a readonly form control field that
 * has a link for its content.
 */
export const LabelledLink = ({
  'aria-describedby': ariaDescribedBy,
  children,
  columnSpan,
  href,
  label,
  labelEdge: propLabelEdge,
  labelStartWidth: propLabelStartWidth,
  lineClamp,
  onClick: propOnClick,
  target,
  textAlign: propTextAlign,
  userAssistanceDensity: propUserAssistanceDensity
}: LabelledLinkProps) => {
  // Consume the form context to get the default values
  const {
    isFormLayout,
    isReadonly: isFormReadonly,
    labelEdge: formLabelEdge,
    labelStartWidth: formLabelStartWidth,
    textAlign: formTextAlign,
    userAssistanceDensity: formUserAssistanceDensity
  } = useFormContext();

  // default to FormContext values if component properties are not specified
  const labelEdge = propLabelEdge ?? formLabelEdge;
  const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
  // JET-72775 - Form control alignment in tables
  // We do not have an embedded mode, so always default to 'start' if no text align is set
  const textAlign = propTextAlign ?? formTextAlign ?? 'start';
  const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

  // We will use the URL as text if the link text is not provided as a child
  const linkText = children ?? href;

  // Calculate the text field related props
  const {
    baseId,
    formFieldContext: textFieldFormFieldContext,
    labelProps,
    inputProps,
    userAssistanceProps
  } = useTextField({
    ariaDescribedBy,
    isReadonly: true,
    labelEdge,
    value: linkText
  });

  // Track pointer down so we know if we gained focus from keyboard or mouse.
  const recentPointerRef = useRef<boolean>(false);
  const pointerDownTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const onPointerDown = () => {
    recentPointerRef.current = true;
    clearTimeout(pointerDownTimerRef.current);
    pointerDownTimerRef.current = setTimeout(() => {
      recentPointerRef.current = false;
    }, 300);
  };

  // Truncated tooltips act differently for LabelledLink, so handle it here. We don't want to
  // show the tooltip when the link is clicked (thereby focusing the field), and if the user
  // tabbed onto the link and activated it with the keyboard, we want to close the tooltip.
  const labelRef = labelProps['labelRef'];
  const linkWasActivatedRef = useRef<boolean>(false);
  const [showTruncatedTooltip, setShowTruncatedTooltip] = useState(false);
  const onBlurWithin = useCallback(() => {
    setShowTruncatedTooltip(false);
  }, []);
  const onFocusWithin = useCallback(() => {
    // Ignore the focus event the first time we see it after the link was activated.
    // For instance if the link was clicked and opened a new browser tab, we don't want
    // to show the tooltip again when they return to the current tab.
    if (linkWasActivatedRef.current) {
      linkWasActivatedRef.current = false;
      return;
    }
    // We only calculate isTruncated if focus was gained through keyboard interaction.
    const isTruncated =
      labelEdge === 'inside' && !recentPointerRef.current ? detectTruncation(labelRef) : false;
    setShowTruncatedTooltip(isTruncated);
  }, [labelEdge, labelRef]);
  const {
    focusProps: { onFocusIn: onFocus, onFocusOut: onBlur }
  } = useFocusWithin({
    isDisabled: false,
    onBlurWithin,
    onFocusWithin
  });
  const formFieldContext = useFormFieldContextProps({
    ...textFieldFormFieldContext,
    hasTruncatedLabel: showTruncatedTooltip
  });
  const onClick = useCallback(
    (event: Event) => {
      setShowTruncatedTooltip(false);
      linkWasActivatedRef.current = true;
      propOnClick?.(event);
    },
    [propOnClick]
  );

  // Create the label component and the fieldLabelProps
  const labelComp = <Label {...labelProps}>{label}</Label>;
  const fieldLabelProps = {
    label: labelEdge !== 'none' ? labelComp : undefined,
    labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
    labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined,
    labelText: labelEdge === 'inside' ? label : undefined
  };

  // save space for user assistance if density is 'efficient', even though we don't
  // render user assistance for readonly fields for now.
  const inlineUserAssistance =
    userAssistanceDensity === 'efficient' ? (
      <InlineUserAssistance
        userAssistanceDensity={userAssistanceDensity}
        {...userAssistanceProps}
      />
    ) : undefined;

  // Get the theme classes
  const { classes } = useLabelledLinkTheme({
    hasInsideLabel: labelEdge === 'inside',
    isFormLayout,
    isFormReadonly,
    textAlign
  });

  // Get the aria props
  const hasEmptyLabel = label === '';
  const hasHiddenLabel = labelEdge === 'none';
  const { containerAria, hiddenLabelAria, linkAria } = useLabelledLinkAria({
    'aria-describedby': inputProps['aria-describedby'],
    baseId,
    hasEmptyLabel,
    hasHiddenLabel,
    labelId: labelProps.id
  });

  return (
    <FormFieldContext.Provider value={formFieldContext}>
      <ReadonlyTextField
        columnSpan={columnSpan}
        role="presentation"
        inlineUserAssistance={inlineUserAssistance}
        {...fieldLabelProps}
        mainContent={
          <div
            onBlur={onBlur}
            onFocus={onFocus}
            onPointerDown={onPointerDown}
            class={classes}
            {...containerAria}>
            {hasHiddenLabel && <HiddenLabel {...hiddenLabelAria}>{label}</HiddenLabel>}
            {/**
             * The containing div is a flexbox. So, the text-align will not work for the link
             * content. So, we wrap the link in a 100% block. This way the text align will
             * work as the link is now inside a block and not flexbox.
             */}
            <View width="100%">
              {lineClamp ? (
                <TruncatingLink
                  lineClamp={lineClamp}
                  href={href}
                  target={target}
                  onClick={onClick}
                  {...linkAria}>
                  {linkText}
                </TruncatingLink>
              ) : (
                <Link href={href} target={target} onClick={onClick} {...linkAria}>
                  {linkText}
                </Link>
              )}
            </View>
          </div>
        }
      />
    </FormFieldContext.Provider>
  );
};
